import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, PencilLine, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../api';
import { formatMelbourneShortDate } from '../../../utils/melbourneTime';

/**
 * "Update property info" on a seller lead (Christian Gravias, DOMA 13460623858:
 * "In the prospects tab next to property profile add a button 'Update property
 * info'. Have a quick button section where agents can put in Price, Property
 * Type, Bedrooms, Bathrooms. This will help our buyer match settings until we
 * can automate this").
 *
 * Saved on the lead (leads/<id>/property-info/) with who changed it and when.
 * Buyer Match and the Prospects buyer counts read these first. The price is
 * today's figure: the owner's expectation or the agent's estimate, never a
 * past sale.
 */

const FALLBACK_TYPES = ['House', 'Apartment/Unit', 'Townhouse', 'Land', 'House and Land',
  'Block of Units', 'Commercial'];

const url = (leadId) => `lead-management/leads/${leadId}/property-info/`;

const stop = (e) => e.stopPropagation();


const fieldCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40 focus:border-[#55C5D0]';

const PropertyInfoDialog = ({ leadId, address, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  // A form that never loaded must not be saved: it would blank what is on file.
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState(FALLBACK_TYPES);
  const [errors, setErrors] = useState({});
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ price: '', price_basis: 'agent', property_type: '', beds: '', baths: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(url(leadId));
        if (cancelled) return;
        const current = res.data.property_info || {};
        setInfo(current);
        if (Array.isArray(res.data.property_types) && res.data.property_types.length) {
          setTypes(res.data.property_types);
        }
        setForm({
          price: current.price != null ? String(current.price) : '',
          price_basis: current.price_basis || 'agent',
          property_type: current.property_type || '',
          beds: current.beds != null ? String(current.beds) : '',
          baths: current.baths != null ? String(current.baths) : '',
        });
      } catch (error) {
        console.error('Failed to load property info:', error);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const res = await api.patch(url(leadId), {
        price: form.price,
        price_basis: form.price ? form.price_basis : '',
        property_type: form.property_type,
        beds: form.beds,
        baths: form.baths,
      });
      toast.success('Property info saved. Buyer Match will use it.');
      onSaved?.(res.data.property_info);
      onClose();
    } catch (error) {
      const fieldErrors = error?.response?.data?.errors;
      if (fieldErrors) setErrors(fieldErrors);
      else toast.error('Could not save the property info.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4"
      onMouseDown={(e) => { stop(e); onClose(); }} onClick={stop}>
      <form
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onMouseDown={stop}
        onSubmit={save}
        role="dialog"
        aria-label="Update property info"
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-gray-900">Update property info</h3>
            {address && <p className="text-sm text-gray-500 mt-0.5 break-words">{address}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : loadFailed ? (
          <div className="m-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Could not load this property's info. Close this and try again.
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label htmlFor="pi-price" className="block text-xs font-semibold text-gray-600 mb-1">Price ($)</label>
              <input id="pi-price" inputMode="numeric" placeholder="e.g. 950000" value={form.price}
                onChange={set('price')} className={fieldCls} />
              <div className="mt-2 flex gap-2" role="radiogroup" aria-label="Whose price">
                {[['owner', "Owner's expectation"], ['agent', 'My estimate']].map(([value, label]) => (
                  <button key={value} type="button" role="radio" aria-checked={form.price_basis === value}
                    onClick={() => setForm((f) => ({ ...f, price_basis: value }))}
                    className={`rounded-full text-xs font-medium px-3 py-1 border ${
                      form.price_basis === value ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              {errors.price && <p className="mt-1 text-xs text-red-600">{errors.price}</p>}
            </div>
            <div>
              <label htmlFor="pi-type" className="block text-xs font-semibold text-gray-600 mb-1">Property type</label>
              <select id="pi-type" value={form.property_type} onChange={set('property_type')} className={fieldCls}>
                <option value="">Not set</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {errors.property_type && <p className="mt-1 text-xs text-red-600">{errors.property_type}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="pi-beds" className="block text-xs font-semibold text-gray-600 mb-1">Bedrooms</label>
                <input id="pi-beds" type="number" min="0" max="50" value={form.beds} onChange={set('beds')} className={fieldCls} />
                {errors.beds && <p className="mt-1 text-xs text-red-600">{errors.beds}</p>}
              </div>
              <div>
                <label htmlFor="pi-baths" className="block text-xs font-semibold text-gray-600 mb-1">Bathrooms</label>
                <input id="pi-baths" type="number" min="0" max="50" value={form.baths} onChange={set('baths')} className={fieldCls} />
                {errors.baths && <p className="mt-1 text-xs text-red-600">{errors.baths}</p>}
              </div>
            </div>
            {info?.updated_at && (
              <p className="text-xs text-gray-400">
                Last updated {formatMelbourneShortDate(info.updated_at)}{info.updated_by ? ` by ${info.updated_by}` : ''}
              </p>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={loading || loadFailed || saving}
            className="px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

const UpdatePropertyInfoButton = ({ leadId, address, className = '', onSaved }) => {
  const [open, setOpen] = useState(false);
  if (!leadId) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { stop(e); setOpen(true); }}
        title="Price, property type, bedrooms and bathrooms for Buyer Match"
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-100 hover:bg-sky-100 whitespace-nowrap ${className}`}
      >
        <PencilLine className="w-3 h-3" />
        Update property info
      </button>
      {open && createPortal(
        <PropertyInfoDialog leadId={leadId} address={address} onClose={() => setOpen(false)} onSaved={onSaved} />,
        document.body,
      )}
    </>
  );
};

export default UpdatePropertyInfoButton;
